import { MultiSelect } from 'components/form/inputs/MultiSelect'
import {
  KNOWLEDGE_DOC_ASSET_TYPE_FILTER_CHANGE,
  useGeckoAnalytics,
} from 'features/analytics'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { DocumentViewerAssetType } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { useMemo } from 'react'
import styled from 'styled-components'

const AssetTypeFilterContainer = styled.div`
  width: 10rem;
`

// Convert enum value to a readable label (e.g., "off_page_connector" -> "Off Page Connector")
const formatAssetTypeLabel = (type: string): string => {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function AssetTypeFilter() {
  const { assetTypeFilters, setAssetTypeFilters } =
    useKnowledgeDocumentUXContext()
  const { boundingBoxDataState } = useDocumentTabDataContext()
  const { trackEvent } = useGeckoAnalytics()

  // Extract unique asset types from the current document's boxes
  const availableAssetTypes = useMemo(() => {
    const types = new Set<DocumentViewerAssetType>()

    boundingBoxDataState.forEach((page) => {
      if (page) {
        ;[...page.confirmed, ...page.unconfirmed].forEach((box) => {
          if (box.type) {
            types.add(box.type)
          }
        })
      }
    })

    return Array.from(types)
      .filter(
        (type) =>
          type !== DocumentViewerAssetType.ALL &&
          type !== DocumentViewerAssetType.SET_UNKNOWN
      )
      .sort((a, b) =>
        formatAssetTypeLabel(a).localeCompare(formatAssetTypeLabel(b))
      )
  }, [boundingBoxDataState])

  const options = useMemo(() => {
    return availableAssetTypes.map((type) => ({
      label: formatAssetTypeLabel(type),
      value: type,
    }))
  }, [availableAssetTypes])

  const handleAssetTypeFilterChange = (value: string[]) => {
    const newFilters = value as DocumentViewerAssetType[]
    trackEvent(KNOWLEDGE_DOC_ASSET_TYPE_FILTER_CHANGE.title, {
      selected_types: newFilters,
      available_types: availableAssetTypes,
    })
    setAssetTypeFilters(newFilters)
  }

  if (availableAssetTypes.length === 0) {
    return null
  }

  return (
    <AssetTypeFilterContainer>
      <MultiSelect
        options={options}
        onValueChange={handleAssetTypeFilterChange}
        value={assetTypeFilters}
        placeholder='Asset Types'
      />
    </AssetTypeFilterContainer>
  )
}
